import axios from '../../../configs/axios';

// ===================================================================
// Shared course/curriculum types (kept byte-for-byte identical to
// frontend/src/types/course.types.ts).
// ===================================================================

export type LessonType = 'video' | 'quiz' | 'text';
export type LessonSource = 'youtube' | 'vimeo' | 'zoom' | 'iframe';
export type CourseLevel = 'beginner' | 'intermediate' | 'advanced' | 'certification';
export type CourseStatus = 'draft' | 'published';

export interface LessonResource {
  title: string;
  url: string;
  type: 'file' | 'link';
  fileKey?: string;
  fileType?: string;
  fileSize?: string;
}

export interface QuizQuestion {
  id: string;
  prompt: string;
  options: string[];
  correctIndex: number;
}

export interface LessonSettings {
  requireCompletion: boolean;
}

// Per-section release schedule: exact date OR a recurring weekday.
export interface SectionSchedule {
  type: 'date' | 'weekday';
  date?: string; // ISO datetime when type === 'date'
  weekday?: number; // 0 (Sun)..6 (Sat) when type === 'weekday'
}

export interface CourseLesson {
  id: string;
  title: string;
  type: LessonType;
  description?: string;

  // video
  videoUrl?: string;
  source?: LessonSource;
  duration?: string;

  // text
  textBody?: string;

  // quiz
  questions?: QuizQuestion[];
  passPercent?: number;

  resources?: LessonResource[];
  settings: LessonSettings;
}

export interface CourseSection {
  id: string;
  title: string;
  schedule?: SectionSchedule | null;
  lessons: CourseLesson[];
}

export interface Course {
  id: string;
  title: string;
  description?: string;
  category: string;
  level: CourseLevel;
  thumbnailUrl?: string;
  thumbnailKey?: string;
  duration?: string;
  status: CourseStatus;
  tags?: string[];
  sections: CourseSection[];   // canonical; server always returns this normalized
  // `lessons?` intentionally OMITTED from the shared Course type — clients read
  // `sections` only. The server migrates legacy `lessons` into `sections` on read.
  createdAt: string;
  updatedAt: string;
}

// ---- Request DTOs (Admin write side) ----
export interface CreateCourseRequest {
  title: string;
  description?: string;
  category: string;
  level: CourseLevel;
  thumbnailUrl?: string;
  thumbnailKey?: string;
  duration?: string;
  status?: CourseStatus;
  tags?: string[];
  sections: CourseSection[];   // always send sections
}
export interface UpdateCourseRequest extends Partial<CreateCourseRequest> {}

// ---- Progress DTOs (frontend read/write side) ----
export interface CourseProgressDto {
  completedLessonIds: string[];
  quizScores: Record<string, number>;
  enrolledAt: string | null;
  unlockedLessonIds: string[];
  percentComplete: number;
}

export interface SetLessonCompletionRequest {
  lessonId: string;
  completed: boolean;
}

export interface QuizSubmitRequest {
  lessonId: string;
  answers: number[];
}
export interface QuizSubmitResponse {
  scorePercent: number;
  passed: boolean;
  passPercent: number;
  correctCount: number;
  totalQuestions: number;
  correctByQuestion: boolean[];
  completed: boolean;
  progress: CourseProgressDto;
}

export interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
}

export const coursesAPI = {
  getAllCourses: async (search?: string) => {
    const params = search ? { search } : {};
    const response = await axios.get('/admin/courses', { params });
    return response.data;
  },
  getCourseById: async (id: string) => {
    const response = await axios.get(`/admin/courses/${id}`);
    return response.data;
  },
  createCourse: async (data: CreateCourseRequest) => {
    const response = await axios.post('/admin/courses', data);
    return response.data;
  },
  updateCourse: async (id: string, data: UpdateCourseRequest) => {
    const response = await axios.put(`/admin/courses/${id}`, data);
    return response.data;
  },
  deleteCourse: async (id: string) => {
    const response = await axios.delete(`/admin/courses/${id}`);
    return response.data;
  },
  // Upload a lesson resource file to S3.
  uploadResourceFile: async (file: File, courseTitle: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'course-resources');
    formData.append('name', courseTitle || 'resource');
    formData.append('version', '1');
    const response = await axios.post('/s3/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data as {
      fileUrl: string;
      fileName: string;
      fileKey: string;
      fileSize: string;
      fileType: string;
    };
  },

  // Upload a course thumbnail image to S3 (private). Returns the S3 key; the
  // key is later resolved to a presigned URL for display.
  uploadThumbnail: async (file: File, courseTitle: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'course-thumbnails');
    formData.append('name', courseTitle || 'thumbnail');
    formData.append('version', '1');
    const response = await axios.post('/s3/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data as {
      fileUrl: string;
      fileName: string;
      fileKey: string;
    };
  },

  // Resolve a presigned URL to display an uploaded thumbnail.
  getThumbnailUrl: async (key: string): Promise<string> => {
    const response = await axios.get('/admin/courses/thumbnail-url', {
      params: { key },
    });
    return response.data.data.url as string;
  },
};

/** Extract YouTube video id from any common URL form. */
export const getYoutubeId = (url: string): string | null => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{6,})/);
  return m?.[1] ?? null;
};

/** Best-effort thumbnail (YouTube only). */
export const getThumbnailUrl = (url: string, source: string): string | null => {
  if (source === 'youtube' || /youtu\.?be/.test(url)) {
    const id = getYoutubeId(url);
    return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
  }
  return null;
};
